import { useTheme } from "@app/ui";
import { useCallback, useEffect, useRef } from "react";

import { localeForLanguage } from "../../lib/language.js";
import { useDataChangeEffect, watchSlices } from "../../lib/useDataChangeEffect.js";
import { publishWidgetSnapshot } from "../../lib/widget-bridge.js";
import { detectPlatform } from "../../platform.js";
import type { NextInstanceCache } from "../../routes/tasks/next-instances.js";
import { useCalendarsStore, useSettingsStore, useTasksStore, useThemeStore, useTopicsStore } from "../../stores/index.js";
import type { RuleExpansionCache } from "../calendar-view/build-days.js";
import { useEffectivePriorityCtx } from "../priority-matrix/useEffectiveMatrixDisplay.js";
import { buildWidgetSnapshot } from "./widget-snapshot.js";

const DEBOUNCE_MS = 800;

/** Snapshot inputs watched IMPERATIVELY and re-read via `getState()` at publish
 *  time: selecting them into render would re-render Shell (and the whole routed
 *  tree) on every task edit — see `useDataChangeEffect`. */
const SNAPSHOT_DATA = [
  watchSlices(useTasksStore, (s) => [s.tasks, s.rules, s.overrides]),
  watchSlices(useTopicsStore, (s) => [s.topics]),
  watchSlices(useCalendarsStore, (s) => [s.calendars]),
  watchSlices(useThemeStore, (s) => [s.config]),
];

/**
 * Android only: keeps the home-screen widgets fed. Rebuilds the widget snapshot
 * whenever tasks/entities/theme/relevant settings change (debounced, off the
 * edit critical path), flushes on app pause, and republishes on resume, then
 * sends it over the WidgetBridge plugin. The native side re-renders on its own 30-min cycle and
 * re-selects "today" from the 14-day snapshot window, so widgets stay correct
 * across days of the app not being opened. Mount once, in Shell.
 *
 * The snapshot inputs are watched IMPERATIVELY and read via `getState()` when a
 * publish fires — selecting tasks/topics/calendars into render would re-render
 * Shell, and with it the whole routed tree, on every edit.
 */
export function useWidgetSnapshotSync(): void {
  const tasksLoaded = useTasksStore((s) => s.loaded);
  const topicsLoaded = useTopicsStore((s) => s.loaded);
  const calendarsLoaded = useCalendarsStore((s) => s.loaded);
  const themeLoaded = useThemeStore((s) => s.loaded);
  const settingsLoaded = useSettingsStore((s) => s.loaded);
  const dark = useTheme().resolved === "dark";
  const priorityCtx = useEffectivePriorityCtx();
  const cache = useRef<RuleExpansionCache>(new Map());
  const nextCache = useRef<NextInstanceCache>(new Map());
  const loaded = tasksLoaded && topicsLoaded && calendarsLoaded && themeLoaded && settingsLoaded;
  const enabled = detectPlatform() === "capacitor" && loaded;

  // Everything the snapshot is built from is read at PUBLISH time, so the hook
  // never holds a render-scoped copy of the data (see `SNAPSHOT_DATA`).
  const publish = useCallback(() => {
    const { tasks, rules, overrides } = useTasksStore.getState();
    const { language, timeFormat, weekStartsOn, multiDayFocus, agendaDays } = useSettingsStore.getState();
    const snapshot = buildWidgetSnapshot({
      tasks,
      rules,
      overrides,
      topics: useTopicsStore.getState().topics,
      calendars: useCalendarsStore.getState().calendars,
      theme: useThemeStore.getState().config,
      dark,
      language,
      locale: localeForLanguage(language),
      hour12: timeFormat === "12h",
      weekStartsOn,
      multiDayFocus,
      agendaDays,
      priorityCtx,
      now: new Date(),
      cache: cache.current,
      nextCache: nextCache.current,
    });
    publishWidgetSnapshot(JSON.stringify(snapshot));
  }, [dark, priorityCtx]);

  const publishRef = useRef(publish);
  publishRef.current = publish;

  const { flush, cancel, schedule } = useDataChangeEffect(SNAPSHOT_DATA, () => publishRef.current(), {
    debounceMs: DEBOUNCE_MS,
    enabled,
  });

  // First publish after hydration, plus the inputs the watches can't see (the
  // resolved dark mode and the day-anchored priority context).
  useEffect(() => {
    if (enabled) schedule();
  }, [enabled, schedule, dark, priorityCtx]);

  useEffect(() => {
    if (!enabled) return;

    const publishNow = () => {
      cancel();
      publishRef.current();
    };
    const flushPending = flush;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushPending();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", flushPending);

    // Flush before the WebView is suspended so an edit followed by immediately
    // backgrounding the app cannot strand widgets on the previous snapshot.
    // The synchronous web lifecycle listeners cover the gap while the optional
    // Capacitor listener is still loading. Resume also catches background sync
    // and acts as a cheap "new day" probe.
    let removed = false;
    const removeHandles: Array<() => void> = [];
    void import("@capacitor/app").then(({ App }) => {
      for (const registration of [
        App.addListener("pause", flushPending),
        App.addListener("resume", publishNow),
      ]) {
        void registration.then((handle) => {
          if (removed) void handle.remove();
          else removeHandles.push(() => { void handle.remove(); });
        }).catch(() => { /* web lifecycle events still flush pending changes */ });
      }
    }).catch(() => { /* optional native lifecycle bridge was unavailable */ });

    return () => {
      removed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", flushPending);
      for (const remove of removeHandles) remove();
    };
  }, [enabled, flush, cancel]);
}
